import { MentionSearchCriteriaModel } from './mention-search-criteria.model';
import { SavedMentionSearchApiModel } from '../../../api/mentions/model/saved-mention-search.api.model';
import { SearchContextsEnum } from '../enums/search-contexts.enum';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { SavedSearchType } from '../../../Common/MonitorShared/enum/saved-search-type.enum';
import { SnippetAreaEnum } from '../../../api/mentions/enum/snippet-area.enum';
import { AutoSavingConfigModel } from './auto-saving-config.model';

export class MentionSearchModel implements AuditableModel {
    id?: number;
    name: string;
    createdByUserId?: number;
    createdDate?: Date;
    modifiedByUserId?: number;
    modifiedDate?: Date;

    applyTagIds: number[];
    searchCriteria: MentionSearchCriteriaModel;
    type: SavedSearchType;
    snippetArea: SnippetAreaEnum;
    autoTaggingSearch: boolean;
    groupId?: number;
    isFavorite: boolean = false;
    searchIntent: string | null = null;
    autoSavingConfig: AutoSavingConfigModel;

    /**
     * Class constructor
     * @param apiEntity
     * @param noDefaultDate  When TRUE, model will be built without a search date range defined
     */
    constructor(
        apiEntity?: SavedMentionSearchApiModel,
        accountSearch: boolean = false
    ) {
        if (apiEntity) {
            this.constructFromApiModel(apiEntity, accountSearch);
        } else {
            this.constructDefault();
        }
    }

    /**
     * Converts the model to an API model that can be used in API calls
    */
    public toApiModel(): SavedMentionSearchApiModel {
        const apiSearch: SavedMentionSearchApiModel = {
            applyTagIds: this.applyTagIds ? this.applyTagIds.slice() : this.applyTagIds,
            comment: '',
            id: this.id,
            name: this.name,
            pageSize: this.searchCriteria.pageSize,
            query: this.searchCriteria.query.getApiEntity(),
            showSimilar: this.searchCriteria.showSimilar,
            sortField: this.searchCriteria.sortField,
            sortOrder: this.searchCriteria.sortOrder,
            type: this.type,
            snippetArea: this.snippetArea,
            autoTaggingSearch: this.autoTaggingSearch,
            displayGroupId: this.groupId,
            isFavorited: this.isFavorite,
            savedFilterIds: this.searchCriteria.savedFilterIds,
            isWordCloudRequest: this.searchCriteria.isWordCloudRequest,
            // auto saving
            savingEnabled: this.autoSavingConfig?.enabled,
            savingFolders: this.autoSavingConfig?.folderIds,
            savingTags: this.autoSavingConfig?.tagIds,
            savingStart: this.autoSavingConfig?.startDate?.toDate(),
            savingEnd: this.autoSavingConfig?.endDate?.toDate(),
            timezone: this.autoSavingConfig?.timezone,
            searchIntent: this.searchIntent?.trim() ? this.searchIntent.trim() : undefined
        };

        return apiSearch;
    }

    /**
     * Returns a copy of this model reset as if it were not yet saved
     */
    public clone(anonymize: boolean = true, keepHydratedRegions: boolean = false): MentionSearchModel {
        const clone = new MentionSearchModel(this.toApiModel());

        if (anonymize) {
            clone.anonymize();
        } else {
            // getApiEntity doesn't include these, so they need to be manually added
            clone.createdByUserId = this.createdByUserId;
            clone.createdDate = this.createdDate ? new Date(this.createdDate) : null;
            clone.modifiedByUserId = this.modifiedByUserId;
            clone.modifiedDate = this.modifiedDate ? new Date(this.modifiedDate) : null;
            clone.searchCriteria = this.searchCriteria.clone(keepHydratedRegions);
        }

        return clone;
    }

    public anonymize(): void {
        this.id = null;
        this.name = null;
        this.createdByUserId = null;
        this.createdDate = null;
        this.modifiedByUserId = null;
        this.modifiedDate = null;
    }

    /**
     * Determines whether or not the search criteria exist
    */
    public hasSearchCriteria(): boolean {
        const hasSearchCriteria: boolean = this.searchCriteria !== null && typeof this.searchCriteria === 'object';

        return hasSearchCriteria;
    }

    /**
     * Determines whether or not this search
     * covers the provided search context
    */
    public hasMatchingSearchContext(searchContext: SearchContextsEnum): boolean {
        const hasSearchCriteria: boolean = this.hasSearchCriteria();
        let hasMatchingSearchContext: boolean = false;

        if (hasSearchCriteria) {
            hasMatchingSearchContext = this.searchCriteria.hasMatchingSearchContext(searchContext);
        }

        return hasMatchingSearchContext;
    }
    /**
     * Returns whether or not the search is saved
    */
    public isSaved(): boolean {
        return !!this.id;
    }

    /**
     * Returns a boolean value for whether the search has a name or not
    */
    public hasName(): boolean {
        return !!this.name;
    }

    /**
     * Returns a boolean value for whether this MentionSearchModel matches another
     */
    public equals(
        other: MentionSearchModel,
        ignoreDate: boolean = false,
        ignoreDateFacet: boolean = false,
        ignoreShowSimilar: boolean = false,
        ignorePagination: boolean = false
    ): boolean {
        if (!other) {
            return false;
        }

        if (this.id !== other.id) {
            return false;
        }

        if (this.name !== other.name) {
            return false;
        }

        if (!this.applyTagIds !== !other.applyTagIds) {
            return false;
        }

        if (this.applyTagIds) {
            if (this.applyTagIds.length !== other.applyTagIds.length) {
                return false;
            }

            for (let i = 0; i < this.applyTagIds.length; i++) {
                if (this.applyTagIds[i] !== other.applyTagIds[i]) {
                    return false;
                }
            }
        }

        if (this.type !== other.type) {
            return false;
        }

        if (!this.searchCriteria.equals(other.searchCriteria, ignoreDate, ignoreDateFacet, ignoreShowSimilar, ignorePagination)) {
            return false;
        }

        if (this.autoTaggingSearch !== other.autoTaggingSearch) {
            return false;
        }

        if (this.autoSavingConfig && !this.autoSavingConfig.equals(other.autoSavingConfig)) {
            return false;
        }

        if (this.searchIntent?.trim() !== other.searchIntent?.trim()) {
            return false;
        }
        return true;
    }

    /**
     * Constructs the model from the API entity
    */
    constructFromApiModel(apiEntity: SavedMentionSearchApiModel, accountSearch: boolean = false): void {
        this.id = apiEntity.id;
        this.type = apiEntity.type as SavedSearchType || SavedSearchType.MONITORING;
        this.name = apiEntity.name;
        this.applyTagIds = apiEntity.applyTagIds;
        this.searchCriteria = new MentionSearchCriteriaModel(apiEntity, accountSearch);
        this.createdByUserId = apiEntity.createdByUserId;
        this.createdDate = new Date(apiEntity.createdDate);
        this.modifiedByUserId = apiEntity.lastModifiedByUserId;
        this.modifiedDate = apiEntity.lastModifiedDate ? new Date(apiEntity.lastModifiedDate) : null;
        this.snippetArea = apiEntity.snippetArea;
        this.autoTaggingSearch = apiEntity.autoTaggingSearch;
        this.groupId = apiEntity.displayGroupId;
        this.isFavorite = apiEntity.isFavorited;
        this.searchIntent = apiEntity.searchIntent?.trim() ? apiEntity.searchIntent.trim() : null;
        this.autoSavingConfig = new AutoSavingConfigModel(apiEntity);
    }

    /**
     * Constructs the default model
     *
     * @param noDefaultDate  When TRUE, model will be built without a search date range defined
     */
    constructDefault(): void {
        this.searchCriteria = new MentionSearchCriteriaModel();
        this.createdByUserId = null;
        this.createdDate = null;
        this.modifiedByUserId = null;
        this.modifiedDate = null;
        this.type = SavedSearchType.MONITORING;
    }
}
